import { ChangeDetectorRef, Component, Input, OnInit, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DashboardService } from '../../../dashboard/dashboard.service';
import { AdminService } from '../../../admin/admin.service';
import { Platform } from '@angular/cdk/platform';
import { saveAs } from '@progress/kendo-file-saver';
import { EnvironmentService } from '../../../services/environment.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SessionStorageService } from '../../../services/session-storage.service';
import { BookManagementService } from '../../book-management.service';

@Component({
  selector: 'app-policy-alerts',
  templateUrl: './policy-alerts.component.html',
  styleUrls: ['./policy-alerts.component.scss'],
  standalone: false,
})
export class PolicyAlertsComponent implements OnInit, OnDestroy {
  private admnSrvc = inject(AdminService);
  private bmSvc = inject(BookManagementService);
  private cdRef = inject(ChangeDetectorRef);
  private dbs = inject(DashboardService);
  private envSvc = inject(EnvironmentService);
  private pltfrm = inject(Platform);
  private route = inject(Router);
  private snacky = inject(MatSnackBar);
  private ss = inject(SessionStorageService);

  @Input() configType;
  @Input() reportName;
  @Input() headerType;
  private environment;
  private baseUrl: string;
  unsubscribe: Subject<any> = new Subject();
  loading = true;
  fileLoading = false;
  skip = 0;
  take = 10;
  pageSizeOptions = [10, 25, 50, 75, 100];
  total = 0;
  gridData = { headers: [], data: [], total: 0 };
  filterValues = {};
  policyBridges = [];

  ngOnInit(): void {
    this.environment = this.envSvc.get();
    this.baseUrl = this.environment.apiBase;

    this.bmSvc.updateBmWidgets$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(filterVals => {
        this.filterValues = filterVals;
        this.bmSvc.getPolicyBridgeOptions().subscribe(resp => {
          if (resp['UnitPMBridges']) {
            this.policyBridges = resp['UnitPMBridges'];
          }
          this.getUiConfig();
        });
      });
  }

  ngOnDestroy() {
    this.unsubscribe.next(null);
    this.unsubscribe.complete();
  }

  getUiConfig() {
    this.loading = true;
    this.admnSrvc
      .getUIConfigHeaders({
        uiconfig: 'pm-dashboard',
        type: this.configType,
        implementation: 'pm',
      })
      .subscribe({
        next: resp => {
          if (resp && resp['headers']) {
            this.gridData.headers = resp['headers'];
          }
          this.getPolicyAlerts();
        },
        error: err => {
          console.warn(err);
          this.loading = false;
        },
      });
  }

  getPolicyAlerts() {
    const reportParams = {
      take: this.take,
      skip: this.skip,
      search: { unitId: { searchString: this.filterValues['unitId'] } },
      name: this.reportName,
    };

    const selectionVars = [
      'npn',
      'accountType',
      'productType',
      'carrier',
      'productName',
      'userId',
    ];

    selectionVars.map(varName => {
      if (this.filterValues[varName] && this.filterValues[varName].length > 0) {
        reportParams['search'][varName] = {
          selected: this.filterValues[varName],
        };
      }
    });

    if (this.filterValues['start'] && this.filterValues['end']) {
      reportParams['search']['SavedTime'] = {
        start: this.filterValues['start'],
        end: this.filterValues['end'],
      };
    }

    this.dbs.getDynamicReport(reportParams).subscribe({
      next: resp => {
        if (resp && resp['data']) {
          this.gridData.data = resp['data'];
          this.gridData.total = resp['total'];
        }
        this.loading = false;
        this.cdRef.detectChanges();
      },
      error: err => {
        console.warn(err);
        this.loading = false;
      },
    });
  }

  getValidationDisplay(validation, value) {
    let displayVal = value;
    validation.map(valObj => {
      if (valObj['value'] === value) {
        displayVal = valObj['label'];
      }
    });
    return displayVal;
  }

  getAlertName(value) {
    return this.policyBridges?.find(bridge => bridge.BridgeID === value)
      ?.BridgeName;
  }

  dataStateChange(ev) {
    this.loading = true;
    this.take = ev.take;
    this.skip = ev.skip;
    this.getPolicyAlerts();
  }

  downloadData() {
    this.fileLoading = true;
    const reportParams = {
      take: 10000,
      skip: 0,
      search: { unitId: { searchString: this.filterValues['unitId'] } },
      name: this.reportName,
    };

    const selectionVars = [
      'npn',
      'accountType',
      'productType',
      'carrier',
      'productName',
    ];

    selectionVars.map(varName => {
      if (this.filterValues[varName] && this.filterValues[varName].length > 0) {
        reportParams['search'][varName] = {
          selected: this.filterValues[varName],
        };
      }
    });

    if (this.filterValues['start'] && this.filterValues['end']) {
      reportParams['search']['SavedTime'] = {
        start: this.filterValues['start'],
        end: this.filterValues['end'],
      };
    }

    this.dbs.getDynamicReportXlsx(reportParams).subscribe(
      data => {
        if (data) {
          const date = new Date();
          const filename =
            'PolicyAlerts-' +
            (date.getMonth() + 1) +
            date.getDate() +
            date.getFullYear() +
            '.xlsx';
          const iosSafari =
            this.pltfrm.IOS ||
            (navigator?.['userAgentData']?.platform === 'macOS' &&
              navigator.maxTouchPoints > 1);

          let fileData;
          const reader = new FileReader();
          reader.readAsDataURL(data);
          reader.onloadend = () => {
            fileData = reader.result;
            saveAs(fileData, filename, {
              proxyURL: `${this.baseUrl}/util/proxy`,
              proxyTarget: '_self',
              forceProxy: iosSafari,
            });
            this.fileLoading = false;
            this.snacky.open(`Download complete.`, 'Close', {
              duration: 4000,
            });
          };
        } else {
          this.fileLoading = false;
        }
      },
      error => {
        this.snacky.open(
          `Oops! Something went wrong. Please try again.`,
          'Close',
          {
            duration: 4000,
          }
        );
        this.fileLoading = false;
      }
    );
  }

  launchRecord(dataItem) {
    const gridName = 'BookManagementFilters';
    const filterObj = {
      logic: 'and',
      filters: [
        {
          field: 'bridgeid',
          operator: 'selected',
          value: [dataItem.alertName],
        },
      ],
    };
    const selectionVars = [
      {
        selectionVar: 'accountType',
        varMap: 'IRSCode',
      },
      {
        selectionVar: 'productType',
        varMap: 'ProductType',
      },
      {
        selectionVar: 'carrier',
        varMap: 'Carrier',
      },
      {
        selectionVar: 'productName',
        varMap: 'Name',
      },
    ];

    selectionVars.map(selectionObj => {
      if (
        this.filterValues[selectionObj.selectionVar] &&
        this.filterValues[selectionObj.selectionVar].length > 0
      ) {
        filterObj.filters.push({
          field: selectionObj.varMap,
          operator: 'selected',
          value: this.filterValues[selectionObj.selectionVar],
        });
      }
    });

    if (this.filterValues['start'] && this.filterValues['end']) {
      filterObj['search']['SavedTime'] = {
        start: this.filterValues['start'],
        end: this.filterValues['end'],
      };
    }

    if (this.filterValues['unitId']) {
      filterObj['filters'].push({
        field: 'unitID',
        operator: 'selected',
        value: [this.filterValues['unitId']],
      });
    }

    if (this.filterValues['npn']) {
      filterObj['filters'].push({
        field: 'NIPR',
        operator: 'selected',
        value: this.filterValues['npn'],
      });
    }
    if (
      this.filterValues['userId'] &&
      this.filterValues['userId'].filter(userIdItem => userIdItem).length &&
      this.headerType === 'unit'
    ) {
      filterObj['filters'].push({
        field: 'userId',
        operator: 'selected',
        value: this.filterValues['userId'],
      });
    } else if (this.headerType === 'rep') {
      filterObj['filters'].push({
        field: 'userId',
        operator: 'selected',
        value: [this.ss.get('globals').user.id],
      });
    }

    this.ss.set(gridName, filterObj);
    this.route.navigate(['/book-management']);
  }
}
